import { initData, isHosted, cloud } from "../platform/telegram";

/**
 * The Mini App's profile API client — the first *real* data path in the app.
 * `Authorization: tma <initData>` carries Telegram's own signature on the
 * launch payload; the gateway validates it and resolves the same
 * `telegram:<id>` user key the bot's DM path uses, so a step saved here is a
 * fact FroshBot remembers everywhere.
 *
 * Saves must never lose an answer: a failed POST parks the patch in a local
 * queue (merged into the next successful save) and the flow keeps moving.
 */

export const FROSHBOT_API_URL =
  (import.meta.env.VITE_FROSHBOT_API_URL as string | undefined) ??
  "https://o9fjvr8xk2.execute-api.us-east-1.amazonaws.com";

/** Server-side FroshbotProfile, narrowed to what onboarding reads and writes. */
export interface OnboardingProfile {
  onboardingStep: "not_started" | "collecting" | "complete";
  onboardingPromptIndex: number;
  displayName: string | null;
  majorsMinors: string | null;
  interests: string | null;
  hobbies: string | null;
  goals: string | null;
  dreams: string | null;
  school: string | null;
  introPreference: "one_on_one" | "small_group" | null;
  checkinCadence: string | null;
  matchmakingMode: "beta_general" | "summer_class";
  summerClassOptIn: boolean;
  summerClassOnboardingStep: "not_started" | "collecting" | "complete";
  summerClassSchool: string | null;
  summerClassCourse: string | null;
  summerClassSchedule: string | null;
}

export type ProfilePatch = Partial<
  Omit<OnboardingProfile, "onboardingStep"> & { onboardingStep: string }
>;

const PENDING_KEY = "fa-onb-pending";
/** CloudStorage flag — set once onboarding completes, so later launches skip
 * the profile fetch entirely and never show the flow again. */
export const ONBOARDED_FLAG_KEY = "fa-onboarded";

function readPending(): ProfilePatch {
  try {
    return JSON.parse(localStorage.getItem(PENDING_KEY) ?? "{}") as ProfilePatch;
  } catch {
    return {};
  }
}

function writePending(patch: ProfilePatch): void {
  // Parked patches carry *content* only. Step/index markers must never ride the
  // queue: a stale flush replaying "collecting" after the final "complete" save
  // is how a finished profile regresses (FOLLOWUPS 8.11). The finish save
  // retries through its own UI, never through this queue.
  const { onboardingStep: _step, onboardingPromptIndex: _index, ...content } = patch;
  try {
    if (Object.keys(content).length === 0) localStorage.removeItem(PENDING_KEY);
    else localStorage.setItem(PENDING_KEY, JSON.stringify(content));
  } catch {
    /* storage full/blocked: the in-flow state still has the answers */
  }
}

export function hasPendingSaves(): boolean {
  return Object.keys(readPending()).length > 0;
}

/* ------------------------------------------------------------------ */
/* Anonymous web identity (browser front door)                         */
/* ------------------------------------------------------------------ */

const WEB_ID_KEY = "fa-web-id";

/** Device-scoped anonymous identity for browser onboarding: a v4 UUID minted
 * once and kept in localStorage. Server-side it becomes `web:<uuid>` — a
 * holding row until a Telegram claim absorbs it. */
export function webId(): string | null {
  if (isHosted) return null;
  try {
    let id = localStorage.getItem(WEB_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(WEB_ID_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

function authHeader(): string | null {
  const data = initData();
  if (data) return `tma ${data}`;
  const web = webId();
  return web ? `web ${web}` : null;
}

/* Web waitlist (browser front door only): email capture + per-school rank. */

export interface WaitlistEntry {
  rank: number;
  school: string;
  schoolCount: number;
}

export async function fetchWaitlist(): Promise<WaitlistEntry | null> {
  const auth = authHeader();
  if (!auth) return null;
  try {
    const response = await fetch(`${FROSHBOT_API_URL}/miniapp/waitlist`, {
      headers: { Authorization: auth },
    });
    if (!response.ok) return null;
    const body = (await response.json()) as { entry?: WaitlistEntry | null };
    return body.entry ?? null;
  } catch {
    return null;
  }
}

export async function joinWaitlist(email: string): Promise<WaitlistEntry | null> {
  const auth = authHeader();
  if (!auth) return null;
  try {
    const response = await fetch(`${FROSHBOT_API_URL}/miniapp/waitlist`, {
      method: "POST",
      headers: { Authorization: auth, "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) return null;
    const body = (await response.json()) as { entry?: WaitlistEntry | null };
    return body.entry ?? null;
  } catch {
    return null;
  }
}

/** Telegram-side: absorb a web-onboarded profile named in a claim deep link. */
export async function claimWebProfile(webIdToClaim: string): Promise<boolean> {
  const data = initData();
  if (!data) return false;
  try {
    const response = await fetch(`${FROSHBOT_API_URL}/miniapp/claim`, {
      method: "POST",
      headers: { Authorization: `tma ${data}`, "Content-Type": "application/json" },
      body: JSON.stringify({ webId: webIdToClaim }),
    });
    return response.ok;
  } catch {
    return false;
  }
}

export async function fetchProfile(): Promise<OnboardingProfile | null> {
  const auth = authHeader();
  if (!auth) return null;
  try {
    const response = await fetch(`${FROSHBOT_API_URL}/miniapp/profile`, {
      headers: { Authorization: auth },
    });
    if (!response.ok) return null;
    const body = (await response.json()) as { profile?: OnboardingProfile };
    return body.profile ?? null;
  } catch {
    return null;
  }
}

/**
 * Saves one step's answers, folding in anything still queued from earlier
 * failures. Returns false when the write did not reach the server — the caller
 * shows the retry affordance, but the answers are already parked locally.
 */
export async function saveStep(patch: ProfilePatch): Promise<boolean> {
  const merged = { ...readPending(), ...patch };

  const auth = authHeader();
  if (!auth) {
    // No identity at all (hosted without initData, or storage blocked in a
    // browser): park locally so the flow stays walkable.
    writePending(merged);
    return !isHosted;
  }

  try {
    const response = await fetch(`${FROSHBOT_API_URL}/miniapp/profile`, {
      method: "POST",
      headers: { Authorization: auth, "Content-Type": "application/json" },
      body: JSON.stringify(merged),
    });
    if (!response.ok) throw new Error(`save failed: ${response.status}`);
    writePending({});
    return true;
  } catch {
    writePending(merged);
    return false;
  }
}

/** Retries the parked queue alone (the "Try again" affordance). */
export async function flushPending(): Promise<boolean> {
  if (!hasPendingSaves()) return true;
  return saveStep({});
}

export async function markOnboarded(): Promise<void> {
  await cloud.set(ONBOARDED_FLAG_KEY, "1");
}

/** Test reset (open with `startapp=reset`): forgets the completion flag, the
 * resume state and the pending queue on this device, so a server-side-wiped
 * account can walk onboarding again. Server data is untouched from here. */
export async function resetLocalState(): Promise<void> {
  await cloud.remove(ONBOARDED_FLAG_KEY);
  clearResume();
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* storage blocked — nothing to clear */
  }
}

export async function isMarkedOnboarded(): Promise<boolean> {
  return (await cloud.get(ONBOARDED_FLAG_KEY)) === "1";
}

/* ------------------------------------------------------------------ */
/* Mid-flow resume state (CloudStorage; survives closing the app)      */
/* ------------------------------------------------------------------ */

const RESUME_KEY = "fa-onb-resume";

export interface ResumeState {
  stepId: string;
  answers: Record<string, unknown>;
}

export async function readResume(): Promise<ResumeState | null> {
  const raw = await cloud.get(RESUME_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as ResumeState;
    return typeof parsed.stepId === "string" ? parsed : null;
  } catch {
    return null;
  }
}

export function writeResume(state: ResumeState): void {
  void cloud.set(RESUME_KEY, JSON.stringify(state));
}

export function clearResume(): void {
  void cloud.remove(RESUME_KEY);
}
